import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCreateCampaign } from '@features/Admin/Campaign/hooks/useCreateCampaign';
import { useAppSelector } from '@shared/store';
import { useUserMeControllerUserMeQuery } from '@shared/services/apiService';
import { useWorkspaceDraftGuard } from './useWorkspaceDraftGuard';
import { useWorkspaceScope } from './useWorkspaceScope';
import { workspaceAgentPath } from './workspacePaths';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';

export const CreateCampaignPage = () => {
  const scope = useWorkspaceScope();
  return <CreateCampaignSession key={scope} />;
};

const CreateCampaignSession = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState('');
  const [direction, setDirection] = useState<'inbound' | 'outbound' | ''>('');
  const [reserved, setReserved] = useState('1');
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const busy = useRef(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const [finished, setFinished] = useState(false);
  const { currentData: me, isError } = useUserMeControllerUserMeQuery();
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const canCreate =
    user?.role !== 'viewer' &&
    me?.companyId === user?.companyId &&
    !!me?.privileges.includes('campaign.manage');
  const { onSubmit, setValue, isLoading, createdCampaignId } = useCreateCampaign({
    onCreated: (id) => {
      if (!active.current) return;
      setFinished(true);
      navigate(
        workspaceAgentPath(
          id,
          'new',
          params.get('job') || undefined,
          params.get('setup') || undefined,
        ),
        { replace: true },
      );
    },
  });
  const discard = useCallback(() => {
    setName('');
    setDirection('');
    setReserved('1');
  }, []);
  const { request, dialog } = useWorkspaceDraftGuard(!finished && (!!name || !!direction), discard);
  const submit = async () => {
    if (
      busy.current ||
      !canCreate ||
      uncertain ||
      createdCampaignId ||
      !name.trim() ||
      !direction ||
      !Number.isInteger(Number(reserved)) ||
      Number(reserved) < 1
    )
      return;
    busy.current = true;
    setError('');
    setValue('title', name.trim());
    setValue('type', direction);
    setValue('reservedLimit', Number(reserved));
    try {
      await onSubmit();
    } catch (e) {
      if (!active.current) return;
      const status = typeof e === 'object' && e !== null && 'status' in e ? e.status : undefined;
      // A definite client rejection can be corrected; an ambiguous create must not be replayed.
      setUncertain(
        !(typeof status === 'number' && status >= 400 && status < 500 && status !== 408),
      );
      setError(
        getApiErrorMessage(
          e,
          'Creation was not confirmed. Review your current campaigns before another attempt.',
        ),
      );
    } finally {
      busy.current = false;
    }
  };
  return (
    <Stack gap={3} sx={{ maxWidth: 980, mx: 'auto' }}>
      <Box>
        <Typography component="h1" variant="h3">
          Create a calling campaign
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Choose how conversations start. Next, build the assistant with its job, voice and provider
          settings.
        </Typography>
      </Box>
      {(!canCreate || isError) && (
        <Alert severity="warning">
          Current campaign-management permission is required to create a campaign.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {createdCampaignId && (
        <Alert severity="success">
          The campaign was created.{' '}
          <Button
            onClick={() =>
              navigate(
                workspaceAgentPath(
                  createdCampaignId,
                  'new',
                  params.get('job') || undefined,
                  params.get('setup') || undefined,
                ),
              )
            }>
            Continue configuring this campaign
          </Button>
        </Alert>
      )}
      <Box
        component="fieldset"
        disabled={!canCreate || isLoading || !!createdCampaignId || uncertain}
        sx={{ p: 0, m: 0, border: 0, minWidth: 0 }}>
        <Stack gap={3}>
          <Typography variant="h5">1. Conversation direction</Typography>
          <Box
            role="group"
            aria-label="Campaign direction"
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {(['inbound', 'outbound'] as const).map((kind) => (
              <Paper
                key={kind}
                component="button"
                type="button"
                aria-pressed={direction === kind}
                onClick={() => setDirection(kind)}
                sx={{
                  p: 3,
                  textAlign: 'start',
                  cursor: 'pointer',
                  border: 2,
                  borderColor: direction === kind ? 'primary.main' : 'divider',
                  bgcolor: direction === kind ? 'primary.light' : 'background.paper',
                  color: 'text.primary',
                }}>
                <Typography variant="h5">
                  {kind === 'inbound' ? 'Receive calls' : 'Call your audience'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {kind === 'inbound'
                    ? 'An assistant answers calls on your campaign phone setup.'
                    : 'An assistant calls the customers you add to this campaign.'}
                </Typography>
              </Paper>
            ))}
          </Box>
          <Typography variant="h5">2. Name and starting capacity</Typography>
          <TextField
            label="Campaign name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 120 }}
            required
          />
          <TextField
            label="Reserved concurrent calls"
            value={reserved}
            type="number"
            onChange={(e) => setReserved(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            helperText="The server validates this reservation against current company capacity. This does not start calls."
          />
          <Button
            variant="contained"
            disabled={
              !canCreate ||
              isLoading ||
              !name.trim() ||
              !direction ||
              uncertain ||
              !!createdCampaignId ||
              !Number.isInteger(Number(reserved)) ||
              Number(reserved) < 1
            }
            onClick={() => void submit()}>
            {isLoading ? <CircularProgress size={18} /> : 'Create campaign and configure assistant'}
          </Button>
        </Stack>
      </Box>
      <Button onClick={() => request(() => navigate('/admin/campaign'))}>Cancel creation</Button>
      {dialog}
    </Stack>
  );
};
